import type { Phase, Round } from "@/types"

/*
 * What the facilitator's rail says, from the prototype's PROMPTS and NUDGE.
 *
 * This is coaching, not UI: things to say and things not to do, written for
 * whoever is running the room. It is deliberately only ever shown to the
 * facilitator — the prompts give away what the round is testing.
 *
 * Re-keyed to the round names the model uses. The prototype called them
 * `frame`, `draft`, `place` and `build`.
 */

const PROMPTS: Record<Round, readonly string[]> = {
  lobby: [
    "Say you are not selling any of these cards.",
    "Ask who signs off on the roadmap.",
    "Check the sceptic is in the room.",
  ],
  pains: [
    "Ask for an incident, not a category.",
    "Do not fill the silence.",
    "Note who writes nothing.",
  ],
  job: [
    "A person, not a department.",
    "Read it back to the room.",
    "Vague sentence, vague session.",
  ],
  cards: [
    "Ask why they took it that fast.",
    "Read a Watch out line aloud.",
    "Ask what a sixth card would replace.",
  ],
  priorities: [
    "Take the card, then the room.",
    "Ask the quiet one first.",
    "Place it only when someone has said why.",
  ],
  deep: [
    "Their words, verbatim.",
    "An empty box is a finding.",
    "Push for a number, not a feeling.",
  ],
  wrap: [
    "Send it before they leave.",
    "Book the Phase 1 conversation now.",
    "Read the parked list out loud.",
  ],
}

export function facilitatorPrompts(round: Round): readonly string[] {
  return PROMPTS[round]
}

/*
 * The single line above the prompts: the one thing to get right in this beat.
 *
 * Keyed by round and phase because two rounds change what they are asking for
 * partway through — writing pains is a different job from voting on them, and
 * reading a finished board back is a different job from filling it.
 */
const NUDGES: { [R in Round]: Partial<Record<Phase, string>> & { default: string } } = {
  lobby: { default: "Name the customer before anything else." },
  pains: {
    default: "Let the silence run.",
    write: "Let the silence run.",
    reveal: "Read them out without commenting.",
    vote: "The winner sets the next forty minutes.",
    tally: "The winner sets the next forty minutes.",
  },
  job: { default: "The deck stays shut until this holds." },
  cards: { default: "Every card they turn over is a question you get to ask." },
  priorities: { default: "Do not place a card before the room has argued about it." },
  deep: { default: "An empty box means it is not a use case yet." },
  wrap: { default: "Send it now, not Thursday." },
}

export function nudge(round: Round, phase: Phase): string {
  const perPhase = NUDGES[round]
  return perPhase[phase] ?? perPhase.default
}

/**
 * The board is filled: the nudge changes from how to place a card to what to do
 * with a finished board. The Priorities round has one phase, so the screen
 * decides this rather than the phase doing it.
 */
export const BOARD_FULL_NUDGE = "Read the top-left back to them before moving on."

export const PROMPTS_COPY = {
  panelLabel: "Controls",
  promptsLabel: "Say this",
  nudgeLabel: "This beat",
  notesLabel: "Your notes",
  notesPlaceholder: "Only you can see this. Nothing here reaches the room.",
  show: "Show facilitator controls",
  hide: "Hide facilitator controls",
} as const
